import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import AntiGravityHero from '../components/AntiGravityHero';
import OEMAdvantage from '../components/OEMAdvantage';
import { LucideHeart, LucideZap, LucideShieldCheck, LucideSmartphone } from 'lucide-react';
import { useLenis } from '../LenisContext';

const WHY_CARDS = [
  { icon: <LucideHeart />, title: 'Comfort', text: 'Tailored environments that adapt to your rhythm.' },
  { icon: <LucideZap />, title: 'Energy Saving', text: 'Intelligence that optimizes consumption effortlessly.' },
  { icon: <LucideShieldCheck />, title: 'Security', text: 'Proactive protection for what matters most.' },
  { icon: <LucideSmartphone />, title: 'Convenience', text: 'Complete control from the palm of your hand.' },
];

const InteractiveCard = ({ children, className = "", delay = 0 }) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const cx = (e.clientX - rect.left) / rect.width - 0.5;
    const cy = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: cy * -10, y: cx * 10 });
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay }}
      onMouseMove={onMouseMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      className="perspective-1000 h-full"
    >
      <div
        ref={cardRef}
        style={{
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: 'transform 0.2s ease-out'
        }}
        className={`group p-8 md:p-10 rounded-[3rem] card-premium flex flex-col h-full bg-white/40 border border-bronze-500/10 hover:border-bronze-500/30 transition-all duration-500 ${className}`}
      >
        {children}
      </div>
    </motion.div>
  );
};

const SHOWCASE_PRODUCTS = [
  { name: "Prime Switch Controller", category: "Switches", image: "/product-1.png", specs: ["16-Channel Neural Engine", "Voice-Priority Logic", "Obsidian Glass Finish"] },
  { name: "Quantum Smart Plug", category: "Plugs", image: "/product-2.png", specs: ["32A Surge Filter", "Nano-App Interaction", "Carbon Fiber Housing"] },
  { name: "Curtain Motor Pro", category: "Essentials", image: "/product-3.png", specs: ["Silent Glide Tech", "Schedule Sync", "Manual Override"] },
];

const HomeAutomation = () => {
  const containerRef = useRef(null);

  const lenis = useLenis();

  useEffect(() => {
    // Force scroll to top on mount
    window.scrollTo(0, 0);
    if (lenis?.current) {
      lenis.current.scrollTo(0, { immediate: true });
    }
    
    // Fallback for some browsers/situations
    const timeout = setTimeout(() => {
      window.scrollTo(0, 0);
      if (lenis?.current) {
        lenis.current.scrollTo(0, { immediate: true });
      }
    }, 50);

    return () => clearTimeout(timeout);
  }, [lenis]);

  return (
    <div className="bg-theme text-theme">
      
      {/* ── SECTION 1: ANTI-GRAVITY HERO (Sticky) ── */}
      <div ref={containerRef} className="relative h-[400vh]">
        <div className="sticky top-0 h-screen overflow-hidden">
          <AntiGravityHero containerRef={containerRef} />
        </div>
      </div>

      {/* ── SECTION 2: WHY HOME AUTOMATION ── */}
      <section className="section-full bg-white/30">
        <div className="max-w-7xl w-full px-8 md:px-12">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-serif font-light mb-4">Why Home Automation?</h2>
            <div className="w-20 h-0.5 bg-iotiq-green mx-auto opacity-20" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {WHY_CARDS.map((card, i) => (
              <InteractiveCard key={i} delay={i * 0.1} className="text-center items-center gap-6">
                <div className="w-16 h-16 rounded-[2rem] bg-bronze-500/5 flex items-center justify-center text-2xl text-bronze-500 group-hover:bg-bronze-500/20 transition-colors duration-500">
                  {card.icon}
                </div>
                <h3 className="text-xl font-bold text-bronze-600 transition-colors duration-500 group-hover:text-bronze-700">{card.title}</h3>
                <p className="text-sm text-theme-secondary leading-relaxed font-light">
                  {card.text}
                </p>
              </InteractiveCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 3: OUR ECOSYSTEM ── */}
      <section className="section-full">
        <div className="max-w-7xl w-full px-8 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-light mb-4 text-theme-primary">Explore Your Ecosystem</h2>
            <div className="w-20 h-0.5 bg-iotiq-green mx-auto opacity-20" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 h-[70vh] min-h-[500px]">
            {/* Card 1 — Comfort & Convenience */}
            <motion.div
              whileHover={{ scale: 0.98 }}
              className="relative rounded-[3rem] overflow-hidden group cursor-pointer shadow-2xl"
            >
              <img
                src="https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?auto=format&fit=crop&q=80&w=1500"
                alt="Comfort"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110"
              />
              {/* Hover blur overlay */}
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

              {/* Content pane */}
              <div className="absolute inset-x-0 bottom-0 p-8 flex flex-col justify-end">
                <div className="w-16 h-16 rounded-3xl bg-white/10 backdrop-blur-md flex items-center justify-center mb-6 text-white border border-white/20 shadow-lg group-hover:scale-110 transition-transform duration-500">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-[0.5em] text-gold mb-2 block drop-shadow-md">
                  Smart Living
                </span>
                <h3 className="text-3xl md:text-4xl font-serif text-white mb-4 drop-shadow-md">Comfort &amp; Convenience</h3>
                <p className="text-white/90 mb-8 max-w-sm font-light leading-relaxed drop-shadow-sm">
                  Experience the ultimate living standard with intelligent climate and mood control.
                </p>
                <Link
                  to="/home-automation/comfort-experience"
                  className="w-fit px-8 py-3 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full text-[10px] uppercase tracking-ultra font-bold shadow-lg hover:bg-gold hover:text-black transition-all duration-500"
                >
                  Explore Path
                </Link>
              </div>
            </motion.div>

            {/* Card 2 — Security & Intelligence */}
            <motion.div
              whileHover={{ scale: 0.98 }}
              className="relative rounded-[3rem] overflow-hidden group cursor-pointer shadow-2xl"
            >
              <img
                src="https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&q=80&w=1500"
                alt="Security"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110"
              />
              {/* Hover blur overlay */}
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

              {/* Content pane */}
              <div className="absolute inset-x-0 bottom-0 p-8 flex flex-col justify-end">
                <div className="w-16 h-16 rounded-3xl bg-white/10 backdrop-blur-md flex items-center justify-center mb-6 text-white border border-white/20 shadow-lg group-hover:scale-110 transition-transform duration-500">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-[0.5em] text-gold mb-2 block drop-shadow-md">
                  Smart Security
                </span>
                <h3 className="text-3xl md:text-4xl font-serif text-white mb-4 drop-shadow-md">Security &amp; Intelligence</h3>
                <p className="text-white/90 mb-8 max-w-sm font-light leading-relaxed drop-shadow-sm">
                  Peace of mind, automated. Proactive security that keeps you connected to your home.
                </p>
                <Link
                  to="/home-automation/security-experience"
                  className="w-fit px-8 py-3 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-full text-[10px] uppercase tracking-ultra font-bold shadow-lg hover:bg-gold hover:text-black transition-all duration-500"
                >
                  Explore Path
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: PRODUCTS ── */}
      <section className="section-full bg-iotiq-green/5">
        <div className="max-w-7xl w-full px-8 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
            <div>
              <span className="text-[10px] font-mono uppercase tracking-[0.4em] text-iotiq-green/50 mb-4 block">Product Showcase</span>
              <h2 className="text-4xl md:text-5xl font-serif font-light">The Touch Collection</h2>
            </div>
            <Link to="/products" className="text-[10px] font-mono uppercase tracking-ultra text-iotiq-green border-b border-iotiq-green/20 pb-2">View All Products</Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {SHOWCASE_PRODUCTS.map((p, i) => (
              <InteractiveCard key={i} delay={i * 0.1}>
                <div className="aspect-square bg-iotiq-green/5 rounded-[2.5rem] mb-8 overflow-hidden relative border border-theme transition-colors shadow-inner flex items-center justify-center p-8">
                  <img 
                    src={p.image} 
                    alt={p.name} 
                    className="w-full h-full object-contain relative z-10 transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&q=80&w=500' }}
                  />
                  <div className="absolute inset-0 bg-iotiq-green/[0.02] pointer-events-none" />
                </div>
                
                <span className="text-iotiq-green/40 font-mono text-[9px] uppercase tracking-ultra mb-4 block">{p.category}</span>
                <h3 className="text-2xl font-serif mb-6">{p.name}</h3>

                <ul className="space-y-4 mb-10 flex-grow">
                  {p.specs.map((s, i) => (
                    <li key={i} className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-theme-secondary group-hover:text-iotiq-green transition-colors font-light">
                      <div className="w-1 h-1 rounded-full bg-iotiq-green/20 group-hover:bg-iotiq-green" />
                      {s}
                    </li>
                  ))}
                </ul>

                <button className="w-full py-4 rounded-2xl bg-iotiq-green text-gold font-bold uppercase tracking-ultra text-[10px] shadow-xl hover:scale-[0.98] transition-all duration-500">
                  Configure Device
                </button>
              </InteractiveCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 5: THE IOTIQ ADVANTAGE ── */}
      <OEMAdvantage />

      {/* ── SECTION 6: OUR WORK ── */}
      <section className="py-32 bg-theme">
        <div className="max-w-7xl mx-auto px-8 md:px-12">

          <div>
            <h2 className="text-4xl font-serif mb-16">Our Work</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="group relative aspect-[4/5] rounded-3xl overflow-hidden cursor-pointer">
                  <img 
                    src={`https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&q=80&w=800&sig=${i}`} 
                    className="w-full h-full object-cover transition-transform duration-[1.5s] group-hover:scale-110" 
                    alt="Work"
                  />
                  <div className="absolute inset-0 bg-iotiq-green/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-8">
                    <h4 className="text-white font-bold">Luxe Villa {i}</h4>
                    <p className="text-white/70 text-xs font-light">Hyderabad, TS</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomeAutomation;